/**
 * CP Mapping - Edit — Access `frmReportCashFlowQryCPMapping_result_add`.
 *
 * The selected ERP customer/vender (read-only) is linked to a MISA object;
 * Save writes the chosen `misa_id` onto `cp_vn.misa_code`.
 */
import { Autocomplete, Box, Dialog, DialogContent, TextField } from '@mui/material'
import DialogTitleBar from '../../../../components/DialogTitleBar'
import { useEffect, useMemo, useState } from 'react'
import {
  useGetCashflowLookupQuery,
  useGetCashflowMisaObjectQuery,
  useSaveCashflowMapCpMutation,
  type MapCp,
} from '../../../../store/api/reportCashflowApi'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { FORM_GRID_2COL } from '../../../../theme/responsive'
import { compactDialogContentSx, readonlyFieldSx } from './formHelpers'

interface Props {
  open: boolean
  row: Record<string, unknown> | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

export default function CpMappingEditDialog({ open, row, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const cp = (row ?? {}) as Partial<MapCp>
  const [misa, setMisa] = useState<{ value: string; label: string } | null>(null)
  const [misaInput, setMisaInput] = useState('')
  const [save, { isLoading }] = useSaveCashflowMapCpMutation()

  const { data: misaLookup } = useGetCashflowLookupQuery({ kind: 'misa-object', q: misaInput || undefined }, { skip: !open })

  // Access `misa_name_AfterUpdate` fills the readonly Misa fields from the picked
  // object. Web: resolve the selected misa_id against the full MISA object list
  // (falls back to the saved row values until the list loads).
  const { data: misaObjects } = useGetCashflowMisaObjectQuery(undefined, { skip: !open })
  const selectedMisa = useMemo(() => {
    const id = misa?.value
    if (!id) return null
    return misaObjects?.items?.find((o) => String(o.misaId ?? '') === id) ?? null
  }, [misaObjects, misa])
  const misaCode = selectedMisa?.misaCode ?? (misa ? cp.misaCode ?? '' : '')
  const misaName = selectedMisa?.misaName ?? (misa ? cp.misaName ?? '' : '')
  const misaAddress = selectedMisa?.misaAddress ?? ''
  const misaVat = selectedMisa?.misaVatCode ?? (misa ? cp.misaVat ?? '' : '')
  const misaId = misa?.value ?? ''
  const seededMisa = useMemo(
    () => (cp.misaId ? { value: String(cp.misaId), label: `${cp.misaCode ?? ''} - ${cp.misaName ?? ''}` } : null),
    [cp.misaId, cp.misaCode, cp.misaName],
  )
  const options = useMemo(() => {
    const items = misaLookup?.items ?? []
    if (seededMisa && !items.some((i) => i.value === seededMisa.value)) return [seededMisa, ...items]
    return items
  }, [misaLookup, seededMisa])

  useEffect(() => {
    if (!open) return
    setMisa(seededMisa)
    setMisaInput('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleSave = async () => {
    if (!canEdit) return
    if (!cp.erpCode) {
      window.alert('Please choose CP VN!')
      return
    }
    try {
      await save({ cpCode: String(cp.erpCode), misaId: misa?.value ?? null }).unwrap()
      snack.success('Successfully Updated')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
      snack.error(detail ?? 'Could not save the mapping.')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitleBar
        title="CP Mapping - Edit"
        onSave={handleSave}
        onCancel={onClose}
        saving={isLoading}
        saveDisabled={isLoading || !canEdit || !cp.erpCode}
      />
      <DialogContent sx={compactDialogContentSx}>
        {/* Access `_add` 2-col layout mirrored 1:1:
            LEFT = ERP group  (ERP - CP VN picker + readonly snapshot),
            RIGHT = MISA group (MISA picker + readonly snapshot).
            ERP picker is readonly here — edit is scoped to the selected CP row. */}
        <Box sx={{ ...FORM_GRID_2COL, mt: 0.5 }}>
          {/* Left column — ERP */}
          <TextField label="ERP - CP VN" value={cp.erpCode ? `${cp.erpCode} — ${cp.erpName ?? ''}` : ''} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 } }} />
          <TextField label="ERP Code" value={cp.erpCode ?? ''} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 2 } }} />
          <TextField label="ERP Name" value={cp.erpName ?? ''} size="small" fullWidth multiline minRows={2} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 3 } }} />
          <TextField label="ERP VAT Code" value={cp.erpVat ?? ''} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 4 } }} />
          <TextField label="ERP Address" value={cp.erpAddress ?? ''} size="small" fullWidth multiline minRows={2} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...readonlyFieldSx, gridColumn: { sm: 1 }, gridRow: { sm: 5 } }} />
          {/* Right column — MISA */}
          <Autocomplete
            size="small"
            options={options}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            // Server already filters (by code / name / VAT via q) → don't
            // re-filter client-side, else a VAT query would be hidden.
            filterOptions={(x) => x}
            renderOption={(props, o) => (
              <li {...props} key={o.value}>
                {o.label}
                {(() => {
                  const d = (o as { description?: string | null }).description
                  return d ? ` · VAT: ${d}` : ''
                })()}
              </li>
            )}
            value={misa}
            onChange={(_, v) => setMisa(v)}
            inputValue={misaInput}
            onInputChange={(_, v) => setMisaInput(v)}
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 1 } }}
            renderInput={(p) => <TextField {...p} label="MISA - Vender/Customer" placeholder="Search code, name or VAT…" InputLabelProps={{ shrink: true }} />}
          />
          {/* Access readonly Misa fields (populated by misa_name AfterUpdate). */}
          <TextField label="Misa Code" value={misaCode} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 2 } }} />
          <TextField label="Misa Name" value={misaName} size="small" fullWidth multiline minRows={2} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 3 } }} />
          <TextField label="Misa VAT Code" value={misaVat} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 4 } }} />
          <TextField label="Misa Address" value={misaAddress} size="small" fullWidth multiline minRows={2} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 5 } }} />
          <TextField label="Misa ID" value={misaId} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 6 } }} />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
